// The repos grid's toolbar state — query, status facets, sort direction — and
// the one derivation they exist for. The `createRepoToggle` / `createWebhookTest`
// split applied once more: the route owns what it RENDERS, this owns what it
// filters, and the filter is then a pure function a test can call without a DOM.
//
// CLIENT-SIDE IS TRUE ONLY BECAUSE THE LIST IS EXHAUSTED. `/v1/repos` advertises
// no `q`/`filter`/`sort` (`REPO_LIST_SPEC = {}`) and the route materialises the
// whole enabled set before slicing it, so `useRepoList`'s `exhaust: true` walk is
// what makes filtering here honest. Before it, the search box saw page one alone
// and answered "no matches" for a repo on page two — which is the bug this
// module's whole existence is downstream of, and the reason it must never be
// pointed at a list that pages.

import { createSignal } from 'solid-js';
import type { PolicyStatus } from '../components/PolicyStatusBadge';
import type { RepoRow } from './repos';

export type RepoFilters = {
  readonly query: () => string;
  readonly setQuery: (value: string) => void;
  /** Selected policy-status facets; empty = no fence, not "none match". */
  readonly active: () => ReadonlySet<PolicyStatus>;
  readonly toggleStatus: (status: PolicyStatus) => void;
  readonly asc: () => boolean;
  readonly toggleSort: () => void;
  /** The rows to render, filtered and sorted. */
  readonly visible: (rows: readonly RepoRow[]) => readonly RepoRow[];
};

export function createRepoFilters(): RepoFilters {
  const [query, setQuery] = createSignal('');
  const [active, setActive] = createSignal<ReadonlySet<PolicyStatus>>(new Set());
  const [asc, setAsc] = createSignal(true);

  return {
    query,
    setQuery,
    active,
    toggleStatus: (status) => {
      const next = new Set(active());
      setActive(next.delete(status) ? next : next.add(status));
    },
    asc,
    toggleSort: () => setAsc((v) => !v),
    // `toSorted` would be nicer, but `.sort` here is safe: `.filter` already
    // returned a fresh array, so the caller's rows are never reordered in place.
    visible: (rows) => {
      const q = query().trim().toLowerCase();
      const statuses = active();
      return rows
        .filter(
          (r) =>
            (q === '' || r.full_name.toLowerCase().includes(q)) &&
            (statuses.size === 0 || statuses.has(r.policy_status)),
        )
        .sort((a, b) => (asc() ? 1 : -1) * a.full_name.localeCompare(b.full_name));
    },
  };
}
